import { useToast } from "@sg-protect/components";
import { useIsMutating, useMutation as useTanstackMutation } from "@tanstack/react-query";
import { mutationOptions, queryOptions, useMutation } from "../client.ts";
import type { MigrationProjectSummary } from "../types.ts";

// PLACEHOLDER: poll interval while figures compute; confirm with product
const FIGURES_POLL_INTERVAL_MS = 5000;

interface ProjectSnapshot {
    previousProject: MigrationProjectSummary | undefined;
}

function isProjectSnapshot(value: unknown): value is ProjectSnapshot {
    return typeof value === "object" && value !== null && "previousProject" in value;
}

export function projectsQueryOptions() {
    return queryOptions("get", "/projects");
}

export function projectQueryOptions(projectId: string) {
    return queryOptions("get", "/projects/{projectId}", { params: { path: { projectId } } });
}

export function planQueryOptions(projectId: string) {
    return queryOptions(
        "get",
        "/projects/{projectId}/plan",
        { params: { path: { projectId } } },
        {
            refetchInterval: query =>
                query.state.data?.complexityStatus === "notInitialized" ? FIGURES_POLL_INTERVAL_MS : false
        }
    );
}

export function sessionsQueryOptions(projectId: string) {
    return queryOptions("get", "/projects/{projectId}/sessions", { params: { path: { projectId } } });
}

export function useCreateProjectMutation() {
    return useMutation("post", "/projects", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await client.invalidateQueries({ queryKey: projectsQueryOptions().queryKey, refetchType: "none" });
        }
    });
}

export function useAssociateProjectTenantMutation() {
    return useMutation("put", "/projects/{projectId}/tenant", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            await client.invalidateQueries({
                queryKey: projectQueryOptions(variables.params.path.projectId).queryKey
            });
        }
    });
}

export function updateProjectNameMutationOptions() {
    return mutationOptions("put", "/projects/{projectId}/name", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: projectsQueryOptions().queryKey }),
                client.invalidateQueries({
                    queryKey: projectQueryOptions(variables.params.path.projectId).queryKey
                })
            ]);
        }
    });
}

export function projectDataSourcesQueryOptions(projectId: string) {
    return queryOptions(
        "get",
        "/projects/{projectId}/data-sources",
        { params: { path: { projectId } } },
        {
            refetchInterval: query =>
                query.state.data?.some(({ figures }) => figures.some(({ status }) => status === "notInitialized"))
                    ? FIGURES_POLL_INTERVAL_MS
                    : false
        }
    );
}

function updateProjectScopeMutationOptions() {
    return mutationOptions("put", "/projects/{projectId}/scope", {
        scope: { id: "project-scope" },
        onMutate: async (variables, { client }) => {
            const queryKey = projectQueryOptions(variables.params.path.projectId).queryKey;

            await client.cancelQueries({ queryKey });

            const previousProject = client.getQueryData(queryKey);

            client.setQueryData(queryKey, project => (project ? { ...project, scope: variables.body.scope } : project));

            return { previousProject };
        },
        onError: (_error, variables, onMutateResult, { client }) => {
            const hasQueuedSaves =
                client.isMutating({ mutationKey: updateProjectScopeMutationOptions().mutationKey }) > 1;

            if (!hasQueuedSaves && isProjectSnapshot(onMutateResult)) {
                client.setQueryData(
                    projectQueryOptions(variables.params.path.projectId).queryKey,
                    onMutateResult.previousProject
                );
            }
        },
        onSettled: async (_data, _error, variables, _onMutateResult, { client }) => {
            if (client.isMutating({ mutationKey: updateProjectScopeMutationOptions().mutationKey }) > 1) {
                return;
            }

            void client.invalidateQueries({ queryKey: planQueryOptions(variables.params.path.projectId).queryKey });

            await client.invalidateQueries({
                queryKey: projectQueryOptions(variables.params.path.projectId).queryKey
            });
        }
    });
}

export function useUpdateProjectScopeMutation() {
    const toast = useToast();
    const options = updateProjectScopeMutationOptions();

    return useTanstackMutation({
        ...options,
        onError: (error, variables, onMutateResult, context) => {
            options.onError?.(error, variables, onMutateResult, context);
            // PLACEHOLDER: save error copy; confirm with design
            toast.error("We couldn't update the migration scope. Please try again.");
        }
    });
}

export function useIsUpdatingProjectScope() {
    return useIsMutating({ mutationKey: updateProjectScopeMutationOptions().mutationKey }) > 0;
}

export function deleteProjectMutationOptions() {
    return mutationOptions("delete", "/projects/{projectId}", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await client.invalidateQueries({ queryKey: projectsQueryOptions().queryKey });
        }
    });
}
